import { PermissionData, Portal } from '../../interfaces';
import { pppToPps, ppsToPpp } from './permission-model.util';
import { timeoutPromise } from '../../common/tools/promise';
import { getParametersFromString } from '../../common/tools/util';

const REQUEST_TIMEOUT = 3000;
const HEADER_ACCEPT_READ_LIST = 'application/vnd.1and1.permissions.portal.permission-read.list-v2+json';
const HEADER_CONTENT_TYPE_WRITE_LIST = 'application/vnd.1and1.permissions.portal.permission-write.list-v2+json';
const HEADER_X_UI_RELAY_TARGET_PATH = 'x-ui-relay-target-path';

/**
 * Required parameters for PPS read/write access
 */
export interface PpsConfig {
  accessToken: string;
  touchpointName: string;
  touchpointReference: string;
  xUIApp: string;
  portal: Portal;
}

/**
 * Retrieve and persist permissions using the Portal Permission Service (PPS)
 */
export class PpsService {
  private readonly config: PpsConfig;
  private readonly serviceUrl: string;
  private targetPathParameters: {
    [key: string]: {
      [key: string]: string;
    };
  } = {};

  constructor(serviceUrl: string, config: PpsConfig) {
    if (typeof serviceUrl !== 'string') {
      throw new Error('PpsService: Missing service URL!');
    }
    this.serviceUrl = serviceUrl;
    this.validateConfig(config);
    this.config = config;
  }

  /**
   * Retrieve permission data from PPS
   */
  public async readPermissions(): Promise<PermissionData[]> {
    const fetchInit: RequestInit = {
      method: 'GET',
    };

    const headers = new Headers();
    headers.append('Authorization', `Bearer ${this.config.accessToken}`);
    headers.append('Accept', HEADER_ACCEPT_READ_LIST);
    fetchInit.headers = headers;

    const response = await timeoutPromise(fetch(this.serviceUrl, fetchInit), REQUEST_TIMEOUT, 'Request timed out');

    const params = this.getParametersFromResponse(response);
    /* istanbul ignore next else */
    if (typeof params.identity_type === 'string') {
      this.targetPathParameters[params.identity_type.toUpperCase()] = params;
    }

    let data;
    if (response.status >= 400) {
      return Promise.reject(`Unable to fetch JSON data (HTTP error): ${this.serviceUrl}, ${response.status}, ${response.statusText}`)
    } else {
      try {
        data = await response.json();
      } catch (err) {
        return Promise.reject(`Unable to parse response data from ${this.serviceUrl}: ${err}`);
      }
    }

    if (!(data instanceof Array)) {
      return Promise.reject(`PPS Response from ${this.serviceUrl} has unexpected format: ${data}`);
    }

    return data.map((entry) => ppsToPpp(entry));
  }

  /**
   * Write a list of permissions to PPS
   * @param permissionData
   */
  public async writePermissions(permissionData: PermissionData[]): Promise<void> {
    const ppsData = permissionData.map((entry) => pppToPps(entry, this.config));
    const fetchInit: RequestInit = {
      method: 'POST',
      body: JSON.stringify(ppsData)
    };

    const headers = new Headers();
    headers.append('Authorization', `Bearer ${this.config.accessToken}`);
    headers.append('Content-Type', HEADER_CONTENT_TYPE_WRITE_LIST);
    fetchInit.headers = headers;

    await timeoutPromise(fetch(this.serviceUrl, fetchInit), REQUEST_TIMEOUT, 'Request timed out')
      .then((result) => {
        if (result.status >= 400) {
          return Promise.reject(`POST request to ${this.serviceUrl} failed: ${result.status} ${result.statusText}`);
        }
        return result;
      });
  }

  /**
   * Returns a map of parameters sent with the response to the last permission read request
   * @param identityType
   */
  public getTargetPathParameters(identityType: string): { [key: string]: string } {
    return this.targetPathParameters[identityType] || {};
  }

  /**
   * Returns a map of parameters from the x-ui-relay-target-path header of the given response
   * @param response Response object
   */
  protected getParametersFromResponse(response: Response): { [key: string]: string } {
    let params: { [key: string]: string } = {};
    if (!response || !response.headers) {
      return params;
    }

    let xUiRelayTargetPath: string;
    response.headers.forEach(((value, key) => {
      /* istanbul ignore next else */
      if (key.toLowerCase() === HEADER_X_UI_RELAY_TARGET_PATH && value) {
        xUiRelayTargetPath = value;
      }
    }));

    /* istanbul ignore next else */
    if (xUiRelayTargetPath) {
      params = getParametersFromString(xUiRelayTargetPath);
    }

    return params;
  }

  private validateConfig(config: PpsConfig): void {
    if (!config) {
      throw new Error('PpsService: Missing config object!');
    }

    if (typeof config !== 'object') {
      throw new Error('PpsService: Config must be an object!');
    }

    [
      'accessToken',
      'touchpointName',
      'touchpointReference',
      'xUIApp'
    ].forEach((propertyName) => {
      /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
      const value = (config as any)[propertyName];
      if (typeof value !== 'string' || value.length === 0) {
        throw new Error(`PpsService: Config property ${propertyName} must be a string but received '${value}'`)
      }
    });
  }
}
